// ------------------------------------------------------------------------------
// name: TypicalStatusTagConfig
// author: mudas
// created: 2025/7/16
// ------------------------------------------------------------------------------

import { ReactNode } from 'react';
import {
  FeedbackStatus,
  FileCheckResult,
  WithdrawStatus,
  WorkshopOrderStatus,
  WorkshopRecommendStatus,
  WorkshopReviewStatus,
  WorkshopStatus,
} from 'common/types';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, SyncOutlined } from '@ant-design/icons';

export type StatusType = string | number;

// 使用攻略：
// 1、在此配置中将对应的状态值，添加到对应的图标状态数组中（或者也可以重新添加新的图标配置）
// 2、在应用场景引用图标组件 <TypicalStatusTag status={statusValue}/>
// 3、以下配置可以自由调整顺序位置，不会导致代码异常或失败，只会改变图标渲染结果

export interface StatusConfType {
  icon?: ReactNode;
  color: 'success' | 'processing' | 'error' | 'warning' | 'default' | string;
  status: StatusType[];
}

// 默认情况下，未在 statusConf 中匹配到状态时使用
export const DefaultConf = {
  color: 'default',
  status: [],
};

/**
 * 根据状态值生成统一风格的状态 key 值
 * - 主要针对各种状态值有可能重复的情况，如此尽可能防止状态值重复导致错误匹配结果
 * @param status 状态值数组
 * @param group 可选的前缀key
 */
export function statusKey(status: StatusType[], group?: string): string[] {
  const keys = group ? [group] : [];
  return status.map((s) => [...keys, s].join('-'));
}

// 图标状态配置
export const StatusConf: StatusConfType[] = [
  // 处理中、进行中、运行中等
  {
    color: 'processing',
    icon: <SyncOutlined spin />,
    status: [
      ...statusKey([WorkshopReviewStatus.PENDING], 'WorkshopReviewStatus'),
      ...statusKey([WorkshopRecommendStatus.USING], 'WorkshopRecommendStatus'),
      ...statusKey([WorkshopStatus.ON_SHELF, WorkshopStatus.CREATING], 'WorkshopStatus'),
      ...statusKey([WithdrawStatus.Pending, WithdrawStatus.Processing], 'WithdrawStatus'),
      ...statusKey(
        [WorkshopOrderStatus.PENDING_PAYMENT, WorkshopOrderStatus.PENDING_RECEIPT, WorkshopOrderStatus.PROCESSING],
        'WorkshopOrderStatus',
      ),
      ...statusKey([FeedbackStatus.PENDING], 'FeedbackStatus'),
    ],
  },

  // 成功的、通过的、已完成的
  {
    color: 'success',
    icon: <CheckCircleOutlined />,
    status: [
      ...statusKey([WorkshopReviewStatus.APPROVED], 'WorkshopReviewStatus'),
      ...statusKey([FileCheckResult.Pass], 'FileCheckResult'),
      ...statusKey([WithdrawStatus.Success], 'WithdrawStatus'),
      ...statusKey([WorkshopOrderStatus.COMPLETED], 'WorkshopOrderStatus'),
    ],
  },

  // 失败的、不通过的、已驳回的
  {
    color: 'error',
    icon: <CloseCircleOutlined />,
    status: [
      ...statusKey([WorkshopReviewStatus.REJECTED], 'WorkshopReviewStatus'),
      ...statusKey([FileCheckResult.NotPass], 'FileCheckResult'),
      ...statusKey([WorkshopStatus.DELETED], 'WorkshopStatus'),
      ...statusKey([WithdrawStatus.Failed], 'WithdrawStatus'),
      ...statusKey([WorkshopOrderStatus.REQUEST_TO_TERMINATE], 'WorkshopOrderStatus'),
    ],
  },

  // 警告的、注意的、已撤回的
  {
    color: 'warning',
    icon: <ExclamationCircleOutlined />,
    status: [
      ...statusKey([WorkshopReviewStatus.WITHDRAWN], 'WorkshopReviewStatus'),
      ...statusKey([FileCheckResult.Warning, FileCheckResult.NotDetected], 'FileCheckResult'),
      ...statusKey([WorkshopRecommendStatus.EXPIRED], 'WorkshopRecommendStatus'),
      ...statusKey([WorkshopOrderStatus.PAYMENT_TIMEOUT, WorkshopOrderStatus.REQUEST_TO_CANCEL], 'WorkshopOrderStatus'),
    ],
  },

  // 默认的、普通的、忽略的
  {
    color: 'default',
    status: [
      ...statusKey([WorkshopReviewStatus.WITHDRAWN], 'WorkshopReviewStatus'),
      ...statusKey([FileCheckResult.Ignore], 'FileCheckResult'),
      ...statusKey([WorkshopRecommendStatus.CANCELED], 'WorkshopRecommendStatus'),
      ...statusKey([WorkshopStatus.PENDING_SHELF], 'WorkshopStatus'),
      ...statusKey([WorkshopOrderStatus.CLOSED], 'WorkshopOrderStatus'),
      ...statusKey([FeedbackStatus.RESOLVED], 'FeedbackStatus'),
    ],
  },
];
